import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Crop,
  FlipHorizontal2,
  FlipVertical2,
  Maximize2,
  Minus,
  Move,
  Plus,
  RotateCcw,
  RotateCw,
  Scan,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";

const ASPECT_PRESETS = [
  { key: "original", label: "Original", value: null },
  { key: "square", label: "1:1", value: 1 },
  { key: "2:1", label: "2:1", value: 2 },
  { key: "3:1", label: "3:1", value: 3 },
  { key: "4:1", label: "4:1", value: 4 },
  { key: "16:9", label: "16:9", value: 16 / 9 },
];

function outputSize(aspect, longEdge) {
  const a = Math.max(0.1, Number(aspect) || 1);
  const edge = Math.max(256, Math.min(2400, Number(longEdge) || 1600));
  if (a >= 1) return { width: edge, height: Math.max(1, Math.round(edge / a)) };
  return { width: Math.max(1, Math.round(edge * a)), height: edge };
}

function isTransparent(value) {
  return !value || value === "transparent";
}

/**
 * Reusable, destructive-output/non-destructive-source crop studio.
 *
 * The parent is expected to keep the original uploaded source path separately
 * from the cropped result. That means "Crop current" can always go back to the
 * full source image instead of repeatedly cropping an already cropped file.
 */
export default function ImageCropDialog({
  open,
  onOpenChange,
  file,
  title = "Crop image",
  description = "Pan, zoom and frame the artwork. The original upload is retained so this image can be re-cropped later.",
  onConfirm,
  lockAspect = null,
  defaultAspect = "original",
  defaultFitMode = "contain",
  defaultBackground = "transparent",
  defaultLongEdge = 1600,
  safeArea = false,
}) {
  const [url, setUrl] = useState("");
  const [meta, setMeta] = useState({ w: 1, h: 1 });
  const [aspectKey, setAspectKey] = useState(lockAspect ? "locked" : defaultAspect);
  const [fitMode, setFitMode] = useState(defaultFitMode);
  const [zoom, setZoom] = useState(1);
  const [x, setX] = useState(0);
  const [y, setY] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [flipX, setFlipX] = useState(false);
  const [flipY, setFlipY] = useState(false);
  const [background, setBackground] = useState(defaultBackground);
  const [showSafeArea, setShowSafeArea] = useState(Boolean(safeArea));
  const [busy, setBusy] = useState(false);
  const frameRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    if (!file) {
      setUrl("");
      return undefined;
    }
    const u = URL.createObjectURL(file);
    setUrl(u);
    setZoom(1);
    setX(0);
    setY(0);
    setRotation(0);
    setFlipX(false);
    setFlipY(false);
    setFitMode(defaultFitMode);
    setBackground(defaultBackground);
    setShowSafeArea(Boolean(safeArea));
    setAspectKey(lockAspect ? "locked" : defaultAspect);
    return () => URL.revokeObjectURL(u);
  }, [file, defaultAspect, defaultBackground, defaultFitMode, lockAspect, safeArea]);

  const originalAspect = meta.w > 0 && meta.h > 0 ? meta.w / meta.h : 1;
  const aspect = useMemo(() => {
    if (lockAspect) return Number(lockAspect) || 1;
    const preset = ASPECT_PRESETS.find((p) => p.key === aspectKey);
    return preset?.value || originalAspect || 1;
  }, [aspectKey, lockAspect, originalAspect]);

  const frame = useMemo(() => {
    const maxW = 500;
    const maxH = 390;
    if (aspect >= 1) {
      const width = maxW;
      return { width, height: Math.max(118, Math.min(maxH, width / aspect)) };
    }
    const height = maxH;
    return { height, width: Math.max(150, height * aspect) };
  }, [aspect]);

  const rotatedMeta = rotation % 180 === 0
    ? { w: meta.w, h: meta.h }
    : { w: meta.h, h: meta.w };
  const baseScale = fitMode === "cover"
    ? Math.max(frame.width / rotatedMeta.w, frame.height / rotatedMeta.h)
    : Math.min(frame.width / rotatedMeta.w, frame.height / rotatedMeta.h);
  const renderedW = meta.w * baseScale * zoom;
  const renderedH = meta.h * baseScale * zoom;
  const offsetX = (x / 100) * frame.width * 0.5;
  const offsetY = (y / 100) * frame.height * 0.5;

  const reset = (mode = fitMode) => {
    setFitMode(mode);
    setZoom(1);
    setX(0);
    setY(0);
    setRotation(0);
    setFlipX(false);
    setFlipY(false);
  };

  const onPointerDown = (e) => {
    if (!frameRef.current) return;
    frameRef.current.setPointerCapture?.(e.pointerId);
    dragRef.current = { clientX: e.clientX, clientY: e.clientY, x, y };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current || !frameRef.current) return;
    const rect = frameRef.current.getBoundingClientRect();
    const dx = ((e.clientX - dragRef.current.clientX) / Math.max(1, rect.width)) * 200;
    const dy = ((e.clientY - dragRef.current.clientY) / Math.max(1, rect.height)) * 200;
    setX(Math.max(-200, Math.min(200, dragRef.current.x + dx)));
    setY(Math.max(-200, Math.min(200, dragRef.current.y + dy)));
  };
  const endDrag = () => { dragRef.current = null; };

  const crop = async () => {
    if (!url || !file) return;
    setBusy(true);
    try {
      const img = new Image();
      img.src = url;
      await img.decode();

      const { width, height } = outputSize(aspect, lockAspect ? 1024 : defaultLongEdge);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas is unavailable in this browser");

      if (!isTransparent(background)) {
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, width, height);
      } else {
        ctx.clearRect(0, 0, width, height);
      }

      const rotatedW = rotation % 180 === 0 ? img.naturalWidth : img.naturalHeight;
      const rotatedH = rotation % 180 === 0 ? img.naturalHeight : img.naturalWidth;
      const base = fitMode === "cover"
        ? Math.max(width / rotatedW, height / rotatedH)
        : Math.min(width / rotatedW, height / rotatedH);
      const scale = base * zoom;
      const dx = (x / 100) * width * 0.5;
      const dy = (y / 100) * height * 0.5;

      ctx.save();
      ctx.translate(width / 2 + dx, height / 2 + dy);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.scale(flipX ? -scale : scale, flipY ? -scale : scale);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
      ctx.restore();

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 1));
      if (!blob) throw new Error("Could not create the cropped image");
      const baseName = (file.name || "image").replace(/\.[^.]+$/, "");
      const out = new File([blob], `${baseName}-cropped.png`, { type: "image/png" });
      await onConfirm(out, {
        aspect,
        fitMode,
        zoom,
        x,
        y,
        rotation,
        flipX,
        flipY,
        background,
        width,
        height,
      });
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  const checker = "linear-gradient(45deg,#d4d4d4 25%,transparent 25%),linear-gradient(-45deg,#d4d4d4 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#d4d4d4 75%),linear-gradient(-45deg,transparent 75%,#d4d4d4 75%)";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[94vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Crop className="h-5 w-5" />{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {url && (
          <div className="grid lg:grid-cols-[minmax(0,1fr)_290px] gap-6">
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                <span>Original: {meta.w} × {meta.h}px</span>
                <span>Output: {outputSize(aspect, lockAspect ? 1024 : defaultLongEdge).width} × {outputSize(aspect, lockAspect ? 1024 : defaultLongEdge).height}px</span>
                <span>Drag image to reposition</span>
              </div>
              <div className="min-h-[410px] rounded-lg border border-border bg-muted/30 flex items-center justify-center p-4 overflow-auto">
                <div
                  ref={frameRef}
                  className="relative overflow-hidden border border-border shadow-xl touch-none cursor-move select-none"
                  style={{
                    width: frame.width,
                    height: frame.height,
                    backgroundColor: isTransparent(background) ? "transparent" : background,
                    backgroundImage: isTransparent(background) ? checker : "none",
                    backgroundSize: "20px 20px",
                    backgroundPosition: "0 0,0 10px,10px -10px,-10px 0px",
                  }}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={endDrag}
                  onPointerCancel={endDrag}
                  onWheel={(e) => {
                    e.preventDefault();
                    const delta = e.deltaY > 0 ? -0.08 : 0.08;
                    setZoom((z) => Math.max(0.2, Math.min(6, z + delta)));
                  }}
                >
                  <img
                    src={url}
                    alt="Crop preview"
                    onLoad={(e) => setMeta({ w: e.currentTarget.naturalWidth || 1, h: e.currentTarget.naturalHeight || 1 })}
                    draggable="false"
                    className="absolute max-w-none pointer-events-none"
                    style={{
                      width: renderedW,
                      height: renderedH,
                      left: `calc(50% + ${offsetX}px)`,
                      top: `calc(50% + ${offsetY}px)`,
                      transform: `translate(-50%, -50%) rotate(${rotation}deg) scaleX(${flipX ? -1 : 1}) scaleY(${flipY ? -1 : 1})`,
                      transformOrigin: "center",
                    }}
                  />
                  {showSafeArea && (
                    <>
                      <div className="absolute inset-[10%] rounded-[22%] border-2 border-white/90 shadow-[0_0_0_999px_rgba(0,0,0,.16)] pointer-events-none" />
                      <div className="absolute inset-[20%] rounded-full border border-white/70 pointer-events-none" />
                    </>
                  )}
                  <div className="absolute inset-0 pointer-events-none ring-1 ring-inset ring-black/10" />
                </div>
              </div>
            </div>

            <div className="space-y-5">
              {!lockAspect && (
                <div>
                  <Label>Frame ratio</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {ASPECT_PRESETS.map((preset) => (
                      <Button
                        key={preset.key}
                        type="button"
                        size="sm"
                        variant={aspectKey === preset.key ? "default" : "outline"}
                        onClick={() => { setAspectKey(preset.key); setX(0); setY(0); }}
                      >{preset.label}</Button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label>Framing</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Button type="button" variant={fitMode === "contain" ? "default" : "outline"} onClick={() => reset("contain")}>
                    <Scan className="h-4 w-4 mr-2" />Fit artwork
                  </Button>
                  <Button type="button" variant={fitMode === "cover" ? "default" : "outline"} onClick={() => reset("cover")}>
                    <Maximize2 className="h-4 w-4 mr-2" />Fill frame
                  </Button>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between"><Label>Zoom</Label><span className="text-xs text-muted-foreground">{Math.round(zoom * 100)}%</span></div>
                <div className="flex items-center gap-3 mt-2">
                  <Minus className="h-4 w-4" />
                  <Slider value={[zoom]} min={0.2} max={6} step={0.01} onValueChange={(v) => setZoom(v[0])} />
                  <Plus className="h-4 w-4" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><Label>Horizontal</Label><Slider className="mt-3" value={[x]} min={-200} max={200} step={1} onValueChange={(v) => setX(v[0])} /></div>
                <div><Label>Vertical</Label><Slider className="mt-3" value={[y]} min={-200} max={200} step={1} onValueChange={(v) => setY(v[0])} /></div>
              </div>

              <div>
                <Label>Transform</Label>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  <Button type="button" variant="outline" size="icon" title="Rotate left" onClick={() => setRotation((r) => (r - 90 + 360) % 360)}><RotateCcw className="h-4 w-4" /></Button>
                  <Button type="button" variant="outline" size="icon" title="Rotate right" onClick={() => setRotation((r) => (r + 90) % 360)}><RotateCw className="h-4 w-4" /></Button>
                  <Button type="button" variant={flipX ? "default" : "outline"} size="icon" title="Flip horizontally" onClick={() => setFlipX((v) => !v)}><FlipHorizontal2 className="h-4 w-4" /></Button>
                  <Button type="button" variant={flipY ? "default" : "outline"} size="icon" title="Flip vertically" onClick={() => setFlipY((v) => !v)}><FlipVertical2 className="h-4 w-4" /></Button>
                </div>
              </div>

              <div>
                <Label>Background</Label>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {["transparent", "#ffffff", "#000000"].map((value) => (
                    <button
                      type="button"
                      key={value}
                      onClick={() => setBackground(value)}
                      className={`h-9 px-3 rounded-md border text-xs ${background === value ? "ring-2 ring-accent" : ""}`}
                      style={{ background: value === "transparent" ? "transparent" : value, color: value === "#000000" ? "white" : "inherit" }}
                    >{value === "transparent" ? "Transparent" : value === "#ffffff" ? "White" : "Black"}</button>
                  ))}
                  <input
                    type="color"
                    value={isTransparent(background) ? "#b5a78a" : background}
                    onChange={(e) => setBackground(e.target.value)}
                    className="h-9 w-12 rounded border border-border bg-transparent p-1"
                    title="Custom background"
                  />
                </div>
              </div>

              {safeArea && (
                <Button type="button" variant={showSafeArea ? "default" : "outline"} className="w-full" onClick={() => setShowSafeArea((v) => !v)}>
                  <Move className="h-4 w-4 mr-2" />{showSafeArea ? "Hide icon safe area" : "Show icon safe area"}
                </Button>
              )}

              <Button type="button" variant="ghost" className="w-full" onClick={() => reset(defaultFitMode)}>Reset crop</Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={busy || !file} onClick={crop}>{busy ? "Rendering…" : "Apply crop"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
